import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  Box,
  Alert,
} from "@mui/material";
import tokens from "../../theme/tokens";

const { color, font } = tokens;

// Same set Interview.js offers — kept identical so both surfaces send the same values.
const TIMEZONES = [
  ["UTC", "UTC (Coordinated Universal Time)"],
  ["America/New_York", "ET (Eastern Time)"],
  ["Europe/London", "GMT (Greenwich Mean Time)"],
  ["Europe/Berlin", "CET (Central European Time)"],
  ["Asia/Kolkata", "IST (Indian Standard Time)"],
  ["America/Los_Angeles", "PT (Pacific Time)"],
  ["Australia/Sydney", "AEST (Australian Eastern Standard Time)"],
];

/**
 * Report.js called startBatchCallAPI(jobId, resumeIds) and dropped the call window +
 * timezone, which the API requires — they posted as undefined. The console asks for
 * them here so the batch call is actually well-formed.
 */
const BatchCallDialog = ({ open, onClose, count, missingPhoneCount, loading, onConfirm }) => {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [timezone, setTimezone] = useState("UTC");
  const [error, setError] = useState("");

  const handleConfirm = () => {
    if (!start || !end || !timezone) {
      setError("Please set a call window start, end, and timezone.");
      return;
    }
    setError("");
    onConfirm({ windowStart: start, windowEnd: end, timezone });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ font: `600 15px ${font.sans}` }}>Start AI calls</DialogTitle>
      <DialogContent>
        <Box sx={{ font: `400 12.5px ${font.sans}`, color: color.textSecondary, mb: 2 }}>
          The AI will call <strong>{count}</strong> candidate{count === 1 ? "" : "s"} one by
          one within the window below.
        </Box>

        {missingPhoneCount > 0 && (
          <Alert severity="warning" sx={{ mb: 2, font: `400 12px ${font.sans}` }}>
            {missingPhoneCount} selected candidate{missingPhoneCount === 1 ? " has" : "s have"} no
            phone number and will be skipped.
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 1.5 }}>
          <TextField
            label="Call window start"
            type="time"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
          />
          <TextField
            label="Call window end"
            type="time"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
          />
        </Box>
        <TextField
          label="Timezone"
          select
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          fullWidth
          size="small"
          margin="normal"
        >
          {TIMEZONES.map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>

        {error && (
          <Box sx={{ mt: 1, font: `400 11.5px ${font.sans}`, color: color.danger }}>{error}</Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading}>
          {loading ? "Starting…" : "Start calls"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BatchCallDialog;
